import React from 'react';
import { useTranslation } from 'react-i18next';

import Location from '@r6digital/rapidstor-types/react/entities/Location';

import useRapidStorTracking from '@/core/hooks/useRapidStorTracking';
import { noAvailableUnitText, RapidStorSettings, saveQuoteButtonText } from '@/core/utils/rapidstor';
import { validateEmail } from '@/core/utils/validation';
import { UnitTypeWithImages } from '@/standard-unit-listing/screens';

import Button from '@/standard-unit-listing/components/elements/button';

import { allowsBookOnlineCta } from '@/standard-unit-listing/ctas';

import { isUnitTypeAvailable } from '@/features/rapidstor/helpers';

import classes from './styles.module.scss';

/**
 * Resolves the operator's "no availability" secondary action to an href the DOM can be trusted
 * with, or null when it cannot be.
 *
 * The value is free text from the site's settings and lands directly in an anchor, so only known
 * schemes are let through: an email address (with or without a `mailto:` already on it) is
 * normalised to a single `mailto:`, and anything else has to parse as http(s). `javascript:` and
 * `data:` values are dropped rather than rendered. The legacy storefront got this for free from
 * Angular's own href sanitiser; React does no such thing.
 */
const resolveSecondaryHref = (action: string): string | null => {
    const email = action.replace(/^mailto:/i, '').trim();
    if (validateEmail(email)) return `mailto:${email}`;

    try {
        const { protocol } = new URL(action);
        return protocol === 'http:' || protocol === 'https:' ? action : null;
    } catch {
        // Not a URL at all — including scheme-less values, which would otherwise
        // resolve against the host page rather than the operator's target.
        return null;
    }
};

const isHttpUrl = (value: string): boolean => {
    try {
        const { protocol } = new URL(value);
        return protocol === 'http:' || protocol === 'https:';
    } catch {
        return false;
    }
};

/**
 * Resolves the site-contact CTA — historically always a `tel:` link to the site's own phone
 * number — to an href for whichever type the operator configured. Falls back to that original
 * `tel:` link whenever the configured type/value can't produce a safe destination of its own,
 * including for an untouched site (`type: 'phone'`, no override value) or a `Location` built by
 * hand rather than through `locationTransformer` (fixtures, older cached data), so nothing
 * regresses or throws when `callSiteCta` itself is missing.
 */
const resolveCallSiteCtaHref = (location: Location): string => {
    const telHref = `tel:${location.contactPhone}`;
    const type = location.rapidstor.callSiteCta?.type ?? 'phone';
    const trimmed = location.rapidstor.callSiteCta?.value?.trim();

    if (type === 'phone') return `tel:${trimmed || location.contactPhone}`;
    if (!trimmed) return telHref;
    if (isHttpUrl(trimmed)) return trimmed;

    if (type === 'whatsapp') {
        const digits = trimmed.replace(/\D/g, '');
        return digits ? `https://wa.me/${digits}` : telHref;
    }
    if (type === 'telegram') {
        const handle = trimmed.replace(/^@/, '');
        return handle ? `https://t.me/${handle}` : telHref;
    }

    // type === 'link', and the value wasn't a usable http(s) URL.
    return telHref;
};

export interface CTAGroupProps {
    location: Location;
    unitType: UnitTypeWithImages;
    onBookOnline?: (unitType: UnitTypeWithImages) => void;
    onSendEnquiry?: (unitType: UnitTypeWithImages) => void;
    // 'mobile' / 'desktop' render responsively; omit to always show.
    view?: 'mobile' | 'desktop';
}

function CTAGroup({
    location, unitType, onBookOnline, onSendEnquiry, view,
}: CTAGroupProps) {
    let classNames = '';
    if (view === 'mobile') classNames = classes.mobile;
    else if (view === 'desktop') classNames = classes.desktop;
    const { t } = useTranslation();
    const { trackEvent } = useRapidStorTracking();

    const available = isUnitTypeAvailable(unitType);
    const showBookOnline = available && allowsBookOnlineCta(location);

    // Optional second call to action under the unavailable button, matching the
    // legacy storefront's `noAvailabilityButtons`: an email address in the
    // action becomes a mailto:, an http(s) URL an external link. Both settings
    // have to be filled in — copy with no usable action is a dead link, and an
    // action with no copy has nothing to click.
    const secondaryText = location.rapidstor.noAvailableUnitsSecondaryText?.trim();
    const secondaryAction = location.rapidstor.noAvailableUnitsSecondaryAction?.trim();
    const secondaryHref = secondaryAction ? resolveSecondaryHref(secondaryAction) : null;
    const secondaryIsEmail = secondaryHref?.startsWith('mailto:') ?? false;

    const callSiteCtaHref = resolveCallSiteCtaHref(location);
    const callSiteCtaLabel = location.rapidstor.callSiteCta?.label?.trim() || noAvailableUnitText(location);
    // Not `callSiteCtaType === 'phone'`: a non-phone type with no usable value falls back to the
    // same tel: href below, and a tel: link should never carry `target="_blank"`/`rel="noreferrer"`
    // regardless of which type produced it.
    const callSiteCtaOpensExternally = !callSiteCtaHref.startsWith('tel:');

    return (
        <div className={`${classes.ctaGroup} ${classNames}`}>
            {available ? (
                <>
                    {showBookOnline ? (
                        <Button
                            type="primary"
                            size="medium"
                            onclick={() => onBookOnline && onBookOnline(unitType)}
                        >
                            {t('standardUnitListing:unitType:bookOnline')}
                        </Button>
                    ) : null}
                    {RapidStorSettings(location).showSaveQuoteButton() && (
                        <Button
                            type="black"
                            size="medium"
                            onclick={() => onSendEnquiry && onSendEnquiry(unitType)}
                        >
                            {saveQuoteButtonText(location)}
                        </Button>
                    )}
                </>
            ) : (
                <>
                    <a
                        href={callSiteCtaHref}
                        target={callSiteCtaOpensExternally ? '_blank' : undefined}
                        rel={callSiteCtaOpensExternally ? 'noreferrer' : undefined}
                        onClick={() => {
                            trackEvent('call_site_manager_clicked', {});
                        }}
                    >
                        <Button type="primary" size="medium">
                            {callSiteCtaLabel}
                        </Button>
                    </a>
                    {secondaryText && secondaryHref ? (
                        <a
                            className={classes.secondaryAction}
                            href={secondaryHref}
                            target={secondaryIsEmail ? undefined : '_blank'}
                            rel={secondaryIsEmail ? undefined : 'noreferrer'}
                        >
                            {location.rapidstor.noAvailableUnitsSecondaryText}
                        </a>
                    ) : null}
                </>
            )}
        </div>
    );
}

export default CTAGroup;
